import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'

import { useGlobalOverlay } from '../../context/GlobalOverlayContext'
import theme from '../../styles/theme'
import { getQualityTagByQualityString } from '../../utils/ScoreFitnessValidation'
import type { QualityString } from '../../utils/ScoreFitnessValidation'

// Styled components
const QualityContainer = styled.div<{ isInteractive?: boolean }>`
  position: relative;
  display: inline-flex;
  cursor: ${(props) => (props.isInteractive ? 'pointer' : 'default')};

  ${(props) =>
    props.isInteractive &&
    `
    &:hover {
      opacity: 0.8;
    }
  `}
`

const Tooltip = styled.div`
  background-color: white;
  border: 1px solid ${theme.colors.backgroundDark};
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: ${theme.shadows.medium};
  z-index: 2000;
  width: max-content;
  max-width: 300px;
  margin-top: 5px;
  transform: translateY(-10px);
  font-size: 12px;
  color: ${theme.colors.standardText};
  line-height: 1.4;
`

// Types
interface QualityTagWithTooltipProps {
  quality: QualityString
  statusText?: string | null
}

const QualityTagWithTooltip: React.FC<QualityTagWithTooltipProps> = ({
  quality,
  statusText,
}) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { addOverlay, removeOverlay } = useGlobalOverlay()

  const hasStatusText = statusText && statusText.trim().length > 0
  const tooltipKey = `quality-tooltip-${quality}-${statusText?.substring(0, 20) || 'no-status'}`

  // Clean up the overlay when component unmounts or when tooltip is hidden
  useEffect(() => {
    return () => {
      removeOverlay(tooltipKey)
    }
  }, [removeOverlay, tooltipKey])

  // Handle tooltip visibility
  useEffect(() => {
    if (hasStatusText && showTooltip) {
      const tooltipContent = <Tooltip>{statusText}</Tooltip>

      addOverlay(tooltipKey, tooltipContent, containerRef.current, 'bottom')
    } else {
      removeOverlay(tooltipKey)
    }
  }, [
    showTooltip,
    statusText,
    addOverlay,
    removeOverlay,
    tooltipKey,
    hasStatusText,
  ])

  return (
    <QualityContainer
      ref={containerRef}
      onMouseEnter={() => (hasStatusText ? setShowTooltip(true) : undefined)}
      onMouseLeave={() => (hasStatusText ? setShowTooltip(false) : undefined)}
      isInteractive={!!hasStatusText}
    >
      {getQualityTagByQualityString(quality)}
    </QualityContainer>
  )
}

export default QualityTagWithTooltip
